import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Upload, Loader2, Check, X, AlertTriangle, Sparkles } from "lucide-react";
import client, { money, apiErr, compressImage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const blank = (tripId) => ({ retailer: "", store_code: "", date: new Date().toISOString().slice(0, 10), items: [], subtotal: 0, tax: 0, total: 0, read_mode: "total_only", trip_id: tripId || null });

export default function ReceiptScanner({ tripId, onSaved }) {
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setScanning(true);
    try {
      const b64 = await compressImage(f);
      const { data } = await client.post("/receipts/scan", { image_base64: b64, trip_id: tripId || null });
      setDraft(data.draft);
      toast.success(data.draft.read_mode === "items" ? `Read ${data.draft.items.length} items — please review` : "Couldn't read every line, so we captured the total");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
      setDraft(blank(tripId));
    } finally { setScanning(false); }
  };

  const setItem = (i, patch) => setDraft((d) => ({ ...d, items: d.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) }));
  const removeItem = (i) => setDraft((d) => ({ ...d, items: d.items.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.post("/receipts", draft);
      setDraft(null);
      toast.success(data.prices_learned ? `Saved — ${data.prices_learned} store prices updated from your receipt` : "Receipt saved");
      onSaved?.(data);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const extras = draft?.items?.filter((i) => i.in_plan === false) || [];

  return (
    <>
      <div className="grid grid-cols-2 gap-3" data-testid="receipt-scanner">
        <button type="button" onClick={() => camRef.current?.click()} disabled={scanning} data-testid="receipt-camera-button"
          className="rounded-3xl bg-pp-forest text-white p-5 text-left pp-shadow transition-transform duration-150 hover:scale-[1.02] active:scale-[0.98]">
          {scanning ? <Loader2 className="h-7 w-7 animate-spin" /> : <Camera className="h-7 w-7" />}
          <p className="mt-3 font-extrabold">{scanning ? "Reading receipt…" : "Take a photo"}</p>
          <p className="text-xs text-white/75">Opens your camera</p>
        </button>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={scanning} data-testid="receipt-upload"
          className="rounded-3xl bg-white border-2 border-dashed border-pp-fresh p-5 text-left transition-colors duration-150 hover:bg-pp-fresh/5">
          <Upload className="h-7 w-7 text-pp-forest" />
          <p className="mt-3 font-extrabold text-pp-text">Upload image</p>
          <p className="text-xs text-pp-muted">From your photos</p>
        </button>
      </div>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="receipt-camera-input" />
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid="receipt-file-input" />
      <button type="button" onClick={() => setDraft(blank(tripId))} data-testid="receipt-manual-button"
        className="mt-2 w-full text-center text-sm font-semibold text-pp-forest hover:underline">Or just enter the receipt total</button>

      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review your receipt</DialogTitle>
            <DialogDescription>
              {draft?.read_mode === "items" ? "AI read each line. Fix anything that looks off before saving." : "Enter the store and total from your receipt."}
            </DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-xs text-pp-muted">Store</label>
                  <Input data-testid="receipt-store-input" value={draft.retailer} onChange={(e) => setDraft({ ...draft, retailer: e.target.value })} className="bg-white" /></div>
                <div><label className="text-xs text-pp-muted">Date</label>
                  <Input type="date" value={draft.date || ""} onChange={(e) => setDraft({ ...draft, date: e.target.value })} className="bg-white" /></div>
              </div>
              {draft.items.length > 0 && (
                <div className="rounded-2xl bg-pp-cream p-2 space-y-1.5" data-testid="receipt-items">
                  {draft.items.map((it, i) => (
                    <div key={i} className={`flex items-center gap-2 rounded-xl px-2 py-1.5 ${it.in_plan === false ? "bg-pp-gold/15" : "bg-white"}`} data-testid={`receipt-item-${i}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-pp-text truncate">{it.name}</p>
                        <p className="text-[11px] text-pp-muted truncate">
                          {it.matched_name ? <span className="text-pp-forest"><Sparkles className="inline h-3 w-3" /> {it.matched_name}</span> : it.raw}
                          {it.in_plan === false && " · not on your plan"}
                        </p>
                      </div>
                      <Input type="number" step="0.01" value={it.price} onChange={(e) => setItem(i, { price: +e.target.value })} className="w-20 h-8 bg-white text-sm" data-testid={`receipt-item-price-${i}`} />
                      <button type="button" onClick={() => removeItem(i)} className="p-1 text-pp-muted hover:text-pp-error" aria-label="Remove line"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              )}
              {extras.length > 0 && (
                <p className="text-xs text-[#8A5F00] bg-pp-gold/20 rounded-xl p-2 flex gap-1.5" data-testid="receipt-extras-note">
                  <AlertTriangle className="h-4 w-4 shrink-0" /> {extras.length} extra item{extras.length > 1 ? "s" : ""} ({money(extras.reduce((s, x) => s + (x.price || 0), 0))}) weren't on your plan, so they're left out of your savings comparison.
                </p>
              )}
              <div className="grid grid-cols-3 gap-2">
                {["subtotal", "tax", "total"].map((k) => (
                  <div key={k}><label className="text-xs text-pp-muted capitalize">{k}</label>
                    <Input type="number" step="0.01" data-testid={`receipt-${k}-input`} value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: +e.target.value })} className="bg-white" /></div>
                ))}
              </div>
              <Button onClick={save} disabled={saving || !draft.total} data-testid="save-receipt" className="rounded-full bg-pp-forest w-full h-11">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Save receipt</>}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
